import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Content } from '@amzn/awscat-pattern-library-service-model';
import { v4 as uuid } from 'uuid';

import { useAttachmentManagementService } from '../../contexts/AttachmentManagementServiceContext';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { log } from '../../loggers/Logger';
import {
  AttachmentManagementService,
  AttachmentMonitor,
  AttachmentSnapshot,
} from '../../services/AttachmentManagementService';
import { SaveDraftError } from '../../services/Errors';
import { useContent } from '../v2-hooks/useContent';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

const ALLOWED_ATTACHMENT_EXTENSIONS: readonly string[] = [
  '.pptx',
  '.docx',
  '.xlsx',
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.svg',
];

/** One row's display state — the {@link AttachmentSnapshot} with its id surfaced. */
export interface AttachmentRow {
  readonly attachmentId: string;
  readonly snapshot: AttachmentSnapshot;
}

export interface UploadingFile {
  readonly id: string;
  readonly filename: string;
  readonly status: 'loading' | 'failed';
  readonly progressPercent: number;
}

export interface AttachmentsTabViewModel {
  readonly contentId: string;
  /**
   * Lower-cased extensions (with leading dot, e.g. `.pptx`) that the
   * file picker should accept. Bind to the file input's native
   * `accept` attribute.
   */
  readonly allowedFileExtensions: readonly string[];
  /** One row per id in `content.attachments`, in order. */
  readonly rows: readonly AttachmentRow[];
  /** Per-file upload indicators. Cleared immediately after stageEdit succeeds (row appears in table). */
  readonly uploadingFiles: readonly UploadingFile[];
  /** Dismisses a single upload indicator (e.g. after a failure). */
  readonly clearUploadEntry: (id: string) => void;
  /**
   * Latest dismissable validation error (size, file-type, etc.). Newer
   * errors overwrite older ones — at most one is ever shown. Undefined
   * when nothing has failed validation since the last dismiss.
   */
  readonly validationErrorMessage: string | undefined;
  /** Clears {@link validationErrorMessage}. Used by the dismiss button. */
  readonly clearValidationError: () => void;
  /**
   * Validates `file`, uploads it, appends its id to `content.attachments`,
   * and stages the edit. The upload indicator is cleared immediately after
   * staging so the table shows the new row without delay. Flush is
   * fire-and-forget. Failures (validation, upload, flush) never reject —
   * the View should not rely on rejection for UI feedback.
   *
   * Validation failures (size, type) publish to
   * {@link validationErrorMessage} with no upload attempted, so the View
   * shouldn't render a progress row for them. Upload failures keep the
   * progress row visible with a `failed` status. Flush failures are only
   * logged for now; a dedicated indicator will surface them later.
   */
  readonly addAttachment: (file: File) => Promise<void>;
  /** Non-undefined when the delete-confirmation modal is open. */
  readonly deleteConfirmation: DeleteConfirmationState | undefined;
  /** Opens the delete-confirmation modal for the given attachment id. */
  readonly requestDelete: (attachmentId: string) => void;
  /** Confirms deletion: stages edit without the attachment, flushes, then closes the modal. */
  readonly confirmDelete: () => void;
  /** Dismisses the delete-confirmation modal without deleting. */
  readonly cancelDelete: () => void;
}

export interface DeleteConfirmationState {
  readonly attachmentId: string;
  readonly filename: string | undefined;
}

function hasAllowedExtension(file: File): boolean {
  return ALLOWED_ATTACHMENT_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));
}

function appendAttachment(content: Content, attachmentId: string): Content {
  return { ...content, attachments: [...(content.attachments ?? []), attachmentId] };
}

function removeAttachment(content: Content, attachmentId: string): Content {
  return {
    ...content,
    attachments: (content.attachments ?? []).filter((id) => id !== attachmentId),
  };
}

/**
 * Subscribes to the attachment-management service for every id in
 * `attachmentIds` and returns a map of latest snapshots. Reconciles
 * subscriptions incrementally — on each id-list change only the
 * **added** ids start monitoring and only the **removed** ids stop
 * monitoring; ids that stayed keep their existing monitor and snapshot.
 *
 * `attachmentIds` is expected to be referentially stable across renders
 * when its contents have not changed; the parent VM memoizes it via the
 * `idsKey` trick.
 */
function useAttachmentSnapshots(
  service: AttachmentManagementService,
  contentId: string,
  attachmentIds: readonly string[],
): ReadonlyMap<string, AttachmentSnapshot> {
  const [snapshotsById, setSnapshotsById] = useState<ReadonlyMap<string, AttachmentSnapshot>>(
    new Map(),
  );
  const monitorsByIdRef = useRef<Map<string, AttachmentMonitor>>(new Map());

  // When the id set changes, diff against the currently-monitored ids
  // and reconcile: start one monitor per added id, stopMonitoring on
  // each removed id, leave the rest untouched.
  useEffect(() => {
    const monitorsById = monitorsByIdRef.current;
    const incomingIds = new Set(attachmentIds);

    for (const [existingId, existingMonitor] of monitorsById) {
      if (incomingIds.has(existingId)) continue;
      service.stopMonitoring(existingMonitor);
      monitorsById.delete(existingId);
    }

    for (const id of attachmentIds) {
      if (monitorsById.has(id)) continue;
      const newMonitor = service.monitorAttachment(contentId, id);
      monitorsById.set(id, newMonitor);
      const captureSnapshot = () => {
        setSnapshotsById((prev) => {
          const next = new Map(prev);
          next.set(id, newMonitor.snapshot);
          return next;
        });
      };
      captureSnapshot();
      newMonitor.on('changed', captureSnapshot);
    }

    // Drop entries from the snapshot map for ids that no longer appear.
    setSnapshotsById((prev) => {
      let didChange = false;
      const next = new Map(prev);
      for (const id of next.keys()) {
        if (!incomingIds.has(id)) {
          next.delete(id);
          didChange = true;
        }
      }
      return didChange ? next : prev;
    });
  }, [service, contentId, attachmentIds]);

  // When the host unmounts, stop every monitor we still own.
  useEffect(() => {
    const monitorsById = monitorsByIdRef.current;
    return () => {
      for (const monitor of monitorsById.values()) service.stopMonitoring(monitor);
      monitorsById.clear();
    };
  }, [service]);

  return snapshotsById;
}

/** Presentation state for the v2 "Attachments" tab. */
export function useAttachmentsTabViewModel(): AttachmentsTabViewModel | undefined {
  const editorService = useContentEditorService();
  const attachmentService = useAttachmentManagementService();
  const content = useContent();
  const document = useLoadedDocument();

  const [validationErrorMessage, setValidationErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [uploadingFiles, setUploadingFiles] = useState<readonly UploadingFile[]>([]);

  const clearUploadEntry = useCallback((id: string) => {
    setUploadingFiles((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  // Memoize the id list by content (`|`-joined) so identical id sets
  // yield the same reference across renders; otherwise
  // `useAttachmentSnapshots` would tear down every monitor on every
  // re-render of the parent.
  const idsKey = (content?.attachments ?? []).join('|');
  const attachmentIds = useMemo(() => (idsKey === '' ? [] : idsKey.split('|')), [idsKey]);

  const snapshotsById = useAttachmentSnapshots(
    attachmentService,
    document?.contentId ?? '',
    attachmentIds,
  );
  const rows: readonly AttachmentRow[] = attachmentIds.map((attachmentId) => ({
    attachmentId,
    snapshot: snapshotsById.get(attachmentId) ?? { kind: 'unknown', attachmentId },
  }));

  const clearValidationError = useCallback(() => setValidationErrorMessage(undefined), []);

  // When the user picks a file, validate it, upload, stage the new id
  // (clearing the upload indicator immediately), then flush in the
  // background. The indicator disappears as soon as the row is visible.
  const addAttachment = useCallback(
    async (file: File): Promise<void> => {
      if (!document) return;
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        setValidationErrorMessage(`"${file.name}" is too large. Maximum size is 10 MB.`);
        return;
      }
      if (!hasAllowedExtension(file)) {
        setValidationErrorMessage(
          `"${file.name}" has an unsupported file type. Allowed types: ${ALLOWED_ATTACHMENT_EXTENSIONS.join(', ')}.`,
        );
        return;
      }

      // Take the lock first because the server accepts uploads only from the lock holder.
      try {
        await editorService.acquireLockIfNeeded();
      } catch (error) {
        setValidationErrorMessage(
          error instanceof SaveDraftError
            ? error.message
            : 'Failed to take the edit lock. Please try again.',
        );
        return;
      }

      const entryId = uuid();
      setUploadingFiles((prev) => [
        ...prev,
        { id: entryId, filename: file.name, status: 'loading' as const, progressPercent: 0 },
      ]);

      const reportProgress = (percent: number) => {
        setUploadingFiles((prev) =>
          prev.map((entry) =>
            entry.id === entryId ? { ...entry, progressPercent: percent } : entry,
          ),
        );
      };

      let attachmentId: string;
      try {
        attachmentId = await attachmentService.upload(document.contentId, file, reportProgress);
      } catch (error) {
        // Upload failures are common (e.g. flaky network); surface in the
        // progress row instead of throwing — the caller is fire-and-forget.
        setUploadingFiles((prev) =>
          prev.map((entry) =>
            entry.id === entryId ? { ...entry, status: 'failed' as const } : entry,
          ),
        );
        log.warn({ err: error, filename: file.name }, 'AttachmentsTabViewModel: upload failed');
        return;
      }

      const beforeStage = editorService.content;
      if (!beforeStage) return;
      editorService.stageEdit(appendAttachment(beforeStage, attachmentId));

      // Row is now visible in the table — clear the upload indicator.
      setUploadingFiles((prev) => prev.filter((entry) => entry.id !== entryId));

      // Flush is fire-and-forget: the staged edit is already visible, and a
      // dedicated flush-failure indicator will surface errors later. For
      // now, just log a warning so the staged id stays put.
      void editorService.flush().catch((error) => {
        log.warn({ err: error }, 'AttachmentsTabViewModel: failed to flush after upload');
      });
    },
    [editorService, attachmentService, document],
  );

  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState | undefined>(
    undefined,
  );

  // When the user clicks the trash icon, open the confirmation modal.
  const requestDelete = useCallback(
    (attachmentId: string) => {
      const snapshot = snapshotsById.get(attachmentId);
      const filename =
        snapshot && (snapshot.kind === 'scanning' || snapshot.kind === 'complete')
          ? snapshot.attachment.filename
          : undefined;
      setDeleteConfirmation({ attachmentId, filename });
    },
    [snapshotsById],
  );

  // When the user confirms deletion, stage edit without the attachment and flush.
  const confirmDelete = useCallback(() => {
    if (!deleteConfirmation) return;
    const { attachmentId } = deleteConfirmation;
    setDeleteConfirmation(undefined);

    const beforeStage = editorService.content;
    if (!beforeStage) return;
    editorService.stageEdit(removeAttachment(beforeStage, attachmentId));
    void editorService.flush();
  }, [deleteConfirmation, editorService]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirmation(undefined);
  }, []);

  // A parent-teardown render can transiently clear content/handle before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!content || !document) return undefined;

  return {
    contentId: document.contentId,
    allowedFileExtensions: ALLOWED_ATTACHMENT_EXTENSIONS,
    rows,
    uploadingFiles,
    clearUploadEntry,
    validationErrorMessage,
    clearValidationError,
    addAttachment,
    deleteConfirmation,
    requestDelete,
    confirmDelete,
    cancelDelete,
  };
}
